// DRIPLORE: PDP gallery <-> variant picker bidirectional sync.
//
// Forward (variant click -> media swap) is native Horizon behaviour
// (MediaGallery#handleVariantUpdate replaces the gallery with new HTML).
//
// This file adds:
//   1. Reverse direction: when a user navigates the gallery to a slide whose
//      media is the featured media of one or more variants, programmatically
//      check the matching option-value radios so the variant picker reflects
//      what the customer is looking at. Multi-option products are supported
//      by clicking each option-value radio in turn.
//   2. Loading hint: when any variant-picker input changes, add a `dl-loading`
//      class to the current media-gallery so the CSS fade/blur kicks in until
//      Horizon replaces it with a fresh element.
//
// Loop guard: reverse sync only fires for `event.detail.userInitiated === true`
// so the programmatic select() that runs after a variant update does not loop.

const SLIDESHOW_SELECT_EVENT = 'slideshow:select';
const LOADING_CLASS = 'dl-loading';
const LOADING_TIMEOUT_MS = 1500;

function bindMap(scriptEl) {
  let map;
  try {
    map = JSON.parse(scriptEl.textContent);
  } catch (_) {
    return;
  }
  if (!map || !Array.isArray(map.variants) || map.variants.length === 0) return;

  // Build mediaId -> first matching variantId
  const mediaToVariant = new Map();
  for (const v of map.variants) {
    if (v.fm == null) continue;
    const key = String(v.fm);
    if (!mediaToVariant.has(key)) mediaToVariant.set(key, v.id);
  }
  if (mediaToVariant.size === 0) return;

  const variantById = new Map(map.variants.map((v) => [v.id, v]));

  const sectionEl = scriptEl.closest('.shopify-section') || document.body;
  if (sectionEl.dataset.driploreMvSync === '1') return;
  sectionEl.dataset.driploreMvSync = '1';

  // Reverse sync: slide change -> select matching variant option-values
  sectionEl.addEventListener(
    SLIDESHOW_SELECT_EVENT,
    (event) => {
      const detail = event.detail;
      if (!detail || !detail.userInitiated) return;

      const gallery = event.target?.closest?.('media-gallery');
      if (!gallery) return;
      if (gallery.closest('.shopify-section') !== sectionEl) return;

      const slide = detail.slide;
      if (!slide) return;
      const carrier = slide.matches('[data-media-id]')
        ? slide
        : slide.querySelector('[data-media-id]');
      const mediaId = carrier?.dataset?.mediaId;
      if (!mediaId) return;

      const variantId = mediaToVariant.get(String(mediaId));
      if (variantId == null) return;
      const variant = variantById.get(variantId);
      if (!variant || !Array.isArray(variant.opts)) return;

      const variantPicker = sectionEl.querySelector('variant-picker');
      if (!variantPicker) return;

      // Collect all inputs that need to change (one per option that differs)
      const inputsToCheck = [];
      variant.opts.forEach((value, idx) => {
        if (value == null) return;
        const input = variantPicker.querySelector(
          `input[type="radio"][data-fieldset-index="${idx}"][value="${CSS.escape(String(value))}"]`
        );
        if (!input) return;
        if (input.checked) return;
        if (input.disabled || input.getAttribute('aria-disabled') === 'true') return;
        inputsToCheck.push(input);
      });
      if (inputsToCheck.length === 0) return;

      // Silently flip all but the last input (radio group exclusivity auto-unchecks
      // other radios with the same name). This avoids multiple fetch races inside
      // variant-picker — variantChanged(event) only fires for the input we click().
      for (let i = 0; i < inputsToCheck.length - 1; i++) {
        inputsToCheck[i].checked = true;
      }
      // Fire ONE change event by clicking the last one. variant-picker reads
      // selectedOptionsValues from `input:checked` so all our silent flips are
      // included in the option_values param.
      inputsToCheck[inputsToCheck.length - 1].click();

      // Horizon sets URL to event.target.dataset.variantId (a per-option fallback,
      // not the actual matched variant). Override with the target variant id we know
      // from our map. Defer past Horizon's own yieldToMainThread() history.replaceState.
      window.setTimeout(() => {
        try {
          const url = new URL(window.location.href);
          if (url.searchParams.get('variant') !== String(variantId)) {
            url.searchParams.set('variant', String(variantId));
            window.history.replaceState({}, '', url.toString());
          }
        } catch (_) {}
      }, 250);
    },
    { passive: true }
  );

  // Loading hint: variant-picker change -> add dl-loading on current gallery
  sectionEl.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('variant-picker')) return;
      const gallery = sectionEl.querySelector('media-gallery');
      if (!gallery) return;
      gallery.classList.add(LOADING_CLASS);
      // Safety: clear after timeout in case replaceWith never happens (e.g. fetch failed)
      window.setTimeout(() => {
        gallery.classList?.remove(LOADING_CLASS);
      }, LOADING_TIMEOUT_MS);
    },
    true
  );
}

function init(root = document) {
  root.querySelectorAll('script[data-driplore-media-variant-map]').forEach(bindMap);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  init(event.target || document);
});
